function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);
  
  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      // trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      // details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    // removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initialSlider() {
    new Swiper('.related-swiper', {
      slidesPerView: 4,
      spaceBetween: 30,
      autoplay: {
        delay: 2500,
      },
      breakpoints: {
        1200: {
          slidesPerView: 4,
          spaceBetween: 30,
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 30,
        },
        991: {
          slidesPerView: 3,
          spaceBetween: 30,
        },
        591: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        340: {
          slidesPerView: 2,
          spaceBetween: 10,
        },
      },
    });
    new Swiper('.complementary-slider', {
      slidesPerView: 3,
      spaceBetween: 15,
      autoplay: {
        delay: 2500,
      },
      breakpoints: {
        1200: {
          slidesPerView: 3,
          spaceBetween: 15,
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 15,
        },
        991: {
          slidesPerView: 3,
          spaceBetween: 15,
        },
        591: {
          slidesPerView: 3,
          spaceBetween: 15,
        },
        340: {
          slidesPerView: 2,
          spaceBetween: 10,
        },
      },
    });
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          handleImageLazyLoad();
          handleProductVariant();
          this.initialSlider();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CustomSelect extends HTMLElement {
  constructor() {
    super();

    this.querySelectorAll('.dynamic-select').forEach((selectElement) => {
      this.createCustomSelectFromSelect(selectElement);
    });
  }

  // Function to generate custom select dropdown for a single <select> element
  createCustomSelectFromSelect(selectElement) {
    const selectId = selectElement.id || Math.random().toString(36).substr(2, 9); // Generate unique ID if not present

    // Create the main container div for the custom select
    const customSelectDiv = document.createElement('div');
    customSelectDiv.id = `custom-${selectId}`;
    customSelectDiv.className = 'custom-select';

    // Create the visible part of the custom select
    const selectedDiv = document.createElement('div');
    selectedDiv.className = 'select-selected';
    selectedDiv.textContent = 'Select an option'; // Default placeholder
    customSelectDiv.appendChild(selectedDiv);

    // Get the default selected option based on the `selected` attribute or fallback to first option
    const defaultSelectedOption = selectElement.querySelector('option[selected]') || selectElement.options[0];
    selectedDiv.textContent = defaultSelectedOption.textContent;
  
    var label = document.createElement("span");
    label.innerHTML = selectElement.parentElement.dataset.label;
    
    selectedDiv.appendChild(label);

    customSelectDiv.appendChild(selectedDiv);

    // Create the dropdown items container (hidden by default)
    const itemsDiv = document.createElement('div');
    itemsDiv.className = 'select-items select-hide';
    customSelectDiv.appendChild(itemsDiv);

    // Populate the dropdown items based on the <select> options
    const options = selectElement.options;
    for (let i = 0; i < options.length; i++) {
      const option = options[i];

      // Create a div for each option
      const customOptionDiv = document.createElement('div');
      customOptionDiv.className = 'select-item';
      customOptionDiv.setAttribute('data-value', option.value);
      customOptionDiv.textContent = option.textContent;

      // Add active class to the option if it's the default selected
      if (option.selected) {
        customOptionDiv.classList.add('active');
      }

      // Add click event to update the selected value and add active class
      customOptionDiv.addEventListener('click', function () {
        // Update the selected text in the visible area
        selectedDiv.textContent = this.textContent;
        var label = document.createElement("span");
        label.innerHTML = selectElement.parentElement.dataset.label;
        selectedDiv.appendChild(label);
        selectElement.value = this.getAttribute('data-value');

        selectElement.dispatchEvent(new Event('change'));
        selectElement.dispatchEvent(new Event('click'));

        // Hide the dropdown
        selectedDiv.classList.remove('select-active');
        itemsDiv.classList.add('select-hide');

        // Remove 'active' class from all items
        const allItems = itemsDiv.querySelectorAll('.select-item');
        allItems.forEach((item) => item.classList.remove('active'));

        // Add 'active' class to the clicked item
        this.classList.add('active');
      });

      itemsDiv.appendChild(customOptionDiv);

    }

    // Insert the custom select after the original select element
    selectElement.style.display = 'none'; // Hide the original <select>
    selectElement.parentNode.insertBefore(customSelectDiv, selectElement.nextSibling);
    
    // Add event listeners for toggling the dropdown
    selectedDiv.addEventListener('click', function () {
      itemsDiv.classList.toggle('select-hide');
      selectedDiv.classList.toggle('select-active');
    });

    // Close the dropdown if clicked outside
    document.addEventListener('click', function (event) {
      if (!event.target.closest(`#custom-${selectId}`)) {
        itemsDiv.classList.add('select-hide');
        selectedDiv.classList.remove('select-active');
      }
    });
  }
  // Example: Dynamically adding an option to a specific select element
  addOptionToSelect(selectElement, value, text) {
    const newOption = document.createElement('option');
    newOption.value = value;
    newOption.textContent = text;
    selectElement.appendChild(newOption);

    // Rebuild the custom select divs
    createCustomSelectFromSelect(selectElement);
  }
}

customElements.define('custom-select', CustomSelect);


class quickViewModal extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.updateContentQuickView);
    this.addEventListener('mouseenter', this.onHover);

    const dropdownPoint = this.querySelector(".info");
    if(dropdownPoint){
      const rect = dropdownPoint.getBoundingClientRect();

      if(rect.x < 0){
        dropdownPoint.classList.add("right");
      }else if((rect.x + rect.width) > window.innerWidth){
        dropdownPoint.classList.add("left");
      }
    }

  }

  onHover(){
    if(this.dataset.blockId){
      const hotspotBlocks = document.querySelectorAll(`.image-hotspot-${this.dataset.sectionId} .block-hotspot`);
      hotspotBlocks.forEach((item) => item.classList.remove('active'));
      const targetSlide = document.querySelector(
        `.block-hotspot-${this.dataset.blockId}`
      );
      if (targetSlide) {
        targetSlide.classList.add('active');
        const targetIndex = targetSlide.getAttribute('data-index') - 1;
        var functionName = `slideToIndex${this.dataset.sectionId}`;
        if (typeof window[functionName] === 'function') {
          window[functionName](targetIndex);
        } else {
          console.error(`Function ${functionName} does not exist`);
        }
      }
    }
  }

  updateContentQuickView() {
    const productModal = document.querySelector('#quick-view-modal');
    const modalBackDrop = document.querySelector('.modal-backdrop');
    const productModalContent = document.querySelector('#quick-view-modal .product-content');

    const product = this;

    product.classList.add('loading');

    const productHandle = this.dataset.productHandle;

    const productUrl = '/products/' + this.dataset.productHandle;
    const productFullUrl = productModal.getAttribute('shop-url') + '/products/' + this.dataset.productHandle;


    $.ajax({
      type: 'get',
      url: this.dataset.shopUrl + '/products/' + productHandle + '?view=ajax_quick_view',
      beforeSend: function () {},
      success: function (data) {
        product.classList.remove('loading');
        const parser = new DOMParser();
        const doc = parser.parseFromString(data, 'text/html');
        const productContent = doc.querySelector('.dz-product-detail');
        const productMedia = doc.querySelector('.dz-product-media');

        productModalContent.innerHTML = `<div class="row sp0"><div class="col-md-6"><div class="dz-product-media">${productMedia.innerHTML}</div></div><div class="col-md-6"><div class="dz-product-detail"> ${productContent.innerHTML}</div></div></div>`;

        const title = productModalContent.querySelector('.product-title').outerHTML;

        productModalContent.querySelector('.product-form__buttons .term-checkbox').outerHTML = `
        <div class="custom-checkbox term-checkbox">
        <input type="checkbox" id="term_checkbox3">
        <label class="custom-check-label" for="term_checkbox3">
        I agree with the
        <a href="javascript:void(0);" data-dz-modal="#term-modal3">
        Terms & conditions
        </a>
        </label>
        </div>
        `;
        productModalContent.querySelector('.product-form__buttons #checkout-product').outerHTML = `
          <div id="quick-checkout-product" class="disabled">
            ${productModalContent.querySelector('.product-form__buttons #checkout-product').innerHTML}
          </div>
        `;

        productModalContent.querySelector('.product-title').outerHTML = `<a href="${productFullUrl}">${title}</a>`;

        const swiperThumb = productModalContent.querySelector('.thumbnail-swiper');

        productModalContent.querySelector('.main-swiper').classList.add('modal-main-swiper');
        productModalContent.querySelector('.modal-main-swiper').classList.remove('main-swiper');
        productModalContent.querySelector('.modal-main-swiper').setAttribute('id', 'lightgallery2');
        if (swiperThumb) {
          productModalContent.querySelector('.thumbnail-swiper').classList.add('modal-thumbnail-swiper');
          productModalContent.querySelector('.modal-thumbnail-swiper').classList.remove('thumbnail-swiper');
        }

        handleImageLazyLoad();
        productSoldOut();

        const productModalPrice = document.querySelector('#quick-view-modal .price-wrapper');

        if (jQuery('#lightgallery2').length > 0) {
          lightGallery(document.getElementById('lightgallery2'), {
            plugins: [lgThumbnail, lgZoom, lgVideo],
            selector: '.lg-item',
            thumbnail: true,
            exThumbImage: 'data-src',
          });
        }

        // var thumbSwiper = new Swiper('.modal-thumbnail-swiper', {
        //   slidesPerView: 6,
        //   spaceBetween: 15,
        //   slideToClickedSlide: true,
        //   breakpoints: {
        //     575: {
        //       slidesPerView: 6,
        //     },
        //     320: {
        //       slidesPerView: 4,
        //       spaceBetween: 10,
        //     },
        //   },
        // });
        // Initialize Main Swiper with thumbnails as thumbs
        var mainSwiper = new Swiper('.modal-main-swiper', {
          slidesPerView: 1,
          // allowTouchMove: false,
          // effect: 'fade',
          spaceBetween: 10,
          navigation: {
            nextEl: '.swiper-product-btn-next',
            prevEl: '.swiper-product-btn-prev',
          },

          // thumbs: {
          //   swiper: jQuery('.modal-thumbnail-swiper').length > 0 && thumbSwiper,
          // },
        });

        const isThumbnail = productModalContent.querySelector('.modal-thumbnail-swiper');
        if(!isThumbnail){
          productModalContent.classList.add('no-thumbnail');
        }else{
          productModalContent.classList.remove('no-thumbnail');
        }

        $('#quick-view-modal variant-selects').on('change', function () {
          let values = $(this)
            .find('.product-form__input input:checked')
            .map(function () {
              return $(this).attr('data-option-value-id');
            })
            .get()
            .join(',');

          const requestUrl = productUrl + '?option_values=' + values;
          fetch(requestUrl)
            .then((response) => response.text())
            .then((responseText) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(responseText, 'text/html');
              const productPrice = doc.querySelector('.price-wrapper');
              const productForm = doc.querySelector('product-form');
              const productInventory = doc.querySelector('.product-inventory-info');
              const productSku = doc.querySelector('.product-sku-info');
              const productSale = doc.querySelector('.product-sale-info');
              const productSelectedVariant = doc.querySelector('[data-selected-variant]');
              
              if (productPrice) {
                productModalPrice.innerHTML = productPrice.innerHTML;
              }

              document.querySelector('#quick-view-modal product-form').outerHTML = productForm.outerHTML;
              if (productSelectedVariant) {
                document.querySelector('#quick-view-modal [data-selected-variant]').innerHTML =
                  productSelectedVariant.innerHTML;
              }
              if (productInventory) {
                document.querySelector('#quick-view-modal .product-inventory-info').innerHTML =
                  productInventory.innerHTML;
              }
              if (productSku) {
                document.querySelector('#quick-view-modal .product-sku-info').innerHTML = productSku.innerHTML;
              }
              if (productSale) {
                document.querySelector('#quick-view-modal .product-sale-info').innerHTML = productSale.innerHTML;
              }
              const selectedVariant = document.querySelector(
                '#quick-view-modal variant-selects [data-selected-variant]'
              )?.innerHTML;
               
              if (selectedVariant) {
                const variantFeaturedMediaId = JSON.parse(selectedVariant)?.featured_media?.id;

                
                if (variantFeaturedMediaId) {
                  const targetSlide = document.querySelector(
                    `.modal-main-swiper .swiper-slide[data-media-id="media-${variantFeaturedMediaId}"]`
                  );
                  if (targetSlide) {
                    const targetIndex = [...targetSlide.parentElement.children].indexOf(targetSlide);
                    if (mainSwiper) {
                      mainSwiper.slideTo(targetIndex);
                    }
                  }
                }
              }
            })
            .catch((error) => {
              console.error(error);
            });
        });

        productModal.classList.add('show');
        modalBackDrop.classList.add('show');
        document.body.classList.add('overflow-hidden');

        document.querySelector('.quick-view-close').addEventListener('click', function () {
          document.body.classList.remove('overflow-hidden');
        });
        // modalBackDrop.addEventListener('click', function () {
        //   productModal.classList.remove('show');
        //   this.classList.remove('show');
        //   document.body.classList.remove('overflow-hidden');
        // });
      },
      error: function (xhr, text) {
        product.classList.remove('loading');
        alert($.parseJSON(xhr.responseText).description);
      },
      complete: function () {},
    });
  }
}
customElements.define('quick-view-modal', quickViewModal);


class NewsletterPopup extends HTMLElement {
  constructor() {
    super();

    this.cookieName = 'newsletter-popup-timer';
    this.cookieDuration = this.dataset.cookieDuration; // Time in days
    this.popupDelay = this.dataset.popupDelay;
  }

  connectedCallback() {
    if (!this.hasSeenPopup()) {
      // Show popup if the cookie does not exist
      setTimeout(() => {
        this.showPopup();
      }, this.popupDelay);
    } 

    this.addEventListener('click', (event) => this.onOverlayClick(event));

    // Add event listener for close button
    this.querySelector('.close-btn').addEventListener('click', () => this.hidePopup());
    // Check if the cookie exists

     // Handle form submission
     const form = this.querySelector('#newsletter-form');
     form.addEventListener('submit', (event) => this.onFormSubmit(event));

  }

  showPopup() {
    this.setAttribute('visible', ''); // Set the "visible" attribute
    setTimeout(() => {
      this.classList.add('show');
    },100)
  }

  onFormSubmit(event) {
    event.preventDefault(); // Prevent the default form submission
    this.setCookie(this.cookieName, 'true', this.cookieDuration); // Set the cookie
    this.hidePopup(); // Close the popup
  }

  onOverlayClick(event) {
    // Check if the click is outside the popup content
    const content = this.querySelector('.newsletter-popup-inner');
    if (!content.contains(event.target)) {
      this.hidePopup();
    }
  }

  hidePopup() {
    this.removeAttribute('visible'); // Remove the "visible" attribute
    this.classList.remove('show');
    this.setCookie(this.cookieName, 'true', this.cookieDuration); // Set the cookie
  }

  hasSeenPopup() {
    return document.cookie.split('; ').some((row) => row.startsWith(`${this.cookieName}=`));
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000); // Calculate expiration time
    document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`; // Set cookie
  }
}

// Define the custom element
customElements.define('newsletter-popup', NewsletterPopup);


class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  render() {
    const tabs = Array.from(this.children);
    this.innerHTML = `
      <div class="tab-container">
        <div class="tab-header-wrapper"></div>
        <div class="tab-content-wrapper"></div>
      </div>
    `;

    const tabHeaderWrapper = this.querySelector(".tab-header-wrapper");
    const tabContentWrapper = this.querySelector(".tab-content-wrapper");

    tabs.forEach((tab, index) => {
      let title = tab.dataset.title;
      let content = tab.innerHTML;

      let header = document.createElement("div");
      header.classList.add("tab-header");
      header.textContent = title;
      header.dataset.index = index;

      let contentDiv = document.createElement("div");
      contentDiv.classList.add("tab-content");
      let contentBody = document.createElement("div");
      let contentHeader = document.createElement("div");
      let closeBtn = document.createElement("button");
      contentBody.classList.add("tab-body");
      contentHeader.classList.add("tab-head");
      closeBtn.classList.add("tab-close-btn");
      closeBtn.innerHTML = `<svg id="fi_5343109" height="512" viewBox="0 0 512 512" width="512" xmlns="http://www.w3.org/2000/svg" data-name="Layer 1"><path d="m390.627 54.627-201.372 201.373 201.372 201.373a32 32 0 1 1 -45.254 45.254l-224-224a32 32 0 0 1 0-45.254l224-224a32 32 0 0 1 45.254 45.254z"></path></svg>`;
      contentBody.innerHTML = content;
      contentHeader.innerHTML =  closeBtn.outerHTML + title;
      contentDiv.innerHTML = contentHeader.outerHTML + contentBody.outerHTML;

      tabHeaderWrapper.appendChild(header);
      tabContentWrapper.appendChild(contentDiv);
    });

    this.setupBehavior();
  }

  setupBehavior() {
    const headers = this.querySelectorAll(".tab-header");
    const closeBtn = this.querySelectorAll(".tab-close-btn");
    const contents = this.querySelectorAll(".tab-content");

    const updateLayout = () => {
      closeBtn.forEach((btn,index) => {
        btn.onclick = () => {
          contents[index].classList.remove("active");
        }
      })
      headers.forEach((header, index) => {
        header.onclick = () => {
          headers.forEach(h => h.classList.remove("active"));
          contents.forEach(c => c.classList.remove("active"));

          header.classList.add("active");
          contents[index].classList.add("active");
        };

        if (index === 0) {
          header.classList.add("active");
          contents[index].classList.add("active");
        }
      });
    };

    window.onresize = function() {
      if(window.innerWidth > 768){
        contents.forEach((content) => {
          content.classList.remove('active');
        })
        headers.forEach((header) => {
          header.classList.remove('active');
        })
        if(headers[0]){
          headers[0].classList.add('active');
          contents[0].classList.add('active');
        }
      }else{
        contents.forEach((content) => {
          content.classList.remove('active');
        })
      }
    }
    updateLayout();
    if(window.innerWidth < 769){
      contents.forEach((content) => {
        content.classList.remove('active');
      })
    }
  }
}

customElements.define("product-tab", ProductTabs);


class WishlistCount extends HTMLElement {
  constructor() {
      super();
      this.updateCount();
      document.addEventListener('wishlist-updated', this.updateCount);
  }

  disconnectedCallback() {
      document.removeEventListener('wishlist-updated', this.updateCount);
  }

  getWishlist() {
      return JSON.parse(localStorage.getItem('wishlist')) || [];
  }

  updateCount = () => {
    const count = this.getWishlist().length;
    if (count > 0){
      this.textContent = count;
    }
  };
}

customElements.define('wishlist-count', WishlistCount);

class WishlistButton extends HTMLElement {
    constructor() {
        super();
        this.productId = this.getAttribute('product-id');
        this.productHandle = this.getAttribute('product-handle');
    }

    connectedCallback() {
        this.updateButtonState();
        this.addEventListener('click', this.toggleWishlist);

    }

    disconnectedCallback() {
        this.removeEventListener('click', this.toggleWishlist);
    }

    getWishlist() {
        return JSON.parse(localStorage.getItem('wishlist')) || [];
    }

    saveWishlist(wishlist) {
        localStorage.setItem('wishlist', JSON.stringify(wishlist));
        document.dispatchEvent(new CustomEvent('wishlist-updated', { detail: wishlist }));
    }

    isProductInWishlist() {
        return this.getWishlist().includes(this.productHandle);
    }

    toggleWishlist = () => {
        let wishlist = this.getWishlist();
        const action = this.isProductInWishlist() ? 'Removed from' : 'Added to';
        const type = this.isProductInWishlist() ? 'error' : 'success';

        if (this.isProductInWishlist()) {
            wishlist = wishlist.filter(id => id !== this.productHandle);
        } else {
            wishlist.push(this.productHandle);
        }
        this.saveWishlist(wishlist);
        this.updateButtonState();

        document.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: `Item ${action} wishlist!`, type: type, duration: 3000 }
        }));

    };

    updateButtonState() {
        this.isProductInWishlist() 
            ?
            [
            this.setAttribute("data-dz-tooltip","Remove from Wishlist"),  
            this.classList.add('active')
          ]
          : 
          [
            this.setAttribute("data-dz-tooltip","Add to Wishlist"),  
            this.classList.remove('active')
          ]
    }
}

customElements.define('wishlist-btn', WishlistButton);


class ToastMessage extends HTMLElement {
  constructor() {
      super();
      this.toastContainer = document.createElement('div');
      this.toastContainer.className = 'toast-container';
      document.body.appendChild(this.toastContainer);

      document.addEventListener('show-toast', this.showToast);
  }

  disconnectedCallback() {
      document.removeEventListener('show-toast', this.showToast);
  }

  showToast = (event) => {
      const { message, type = 'success', duration = 3000 } = event.detail;

      const toast = document.createElement('div');
      toast.className = `toast-message ${type}`;
      toast.textContent = message;

      this.toastContainer.appendChild(toast);

      setTimeout(() => {
          toast.classList.add('fade-out');
          setTimeout(() => {
              toast.remove();
          }, 500);
      }, duration);
  };
}

// Define the custom element
customElements.define('toast-message', ToastMessage);



class FreeShippingBar extends HTMLElement {
  constructor() {
      super();
      this.cartTotal = parseFloat(this.getAttribute("cart-total")) || 0;
      this.progressBackground = this.getAttribute("progress-bg-color") || "#eee";
      this.progressText = this.getAttribute("progress-text-color") || "#00000";
      this.progressLevel1 = this.getAttribute("progress-level-1") || "#F39C12";
      this.progressLevel2 = this.getAttribute("progress-level-2") || "#F39C12";
      this.progressLevel3 = this.getAttribute("progress-level-3") || "#3498DB";
      this.progressLevel4 = this.getAttribute("progress-level-4") || "#2ECC71";
      this.showTruck = this.getAttribute("show-truck") || true;
      this.freeShippingThreshold = parseFloat(this.getAttribute("free-shipping-threshold")) || 50;
      this.render();
  }

  getBackgroundColor(progress) {
      if (progress < 30) return this.progressLevel1; 
      if (progress < 60) return this.progressLevel2;
      if (progress < 100) return this.progressLevel3;
      return this.progressLevel4;
  }

  render() {
      let remainingAmount = this.freeShippingThreshold - this.cartTotal;
      let progress = (this.cartTotal / this.freeShippingThreshold) * 100;
      if (progress > 100) progress = 100;

      let bgColor = this.getBackgroundColor(progress);

      this.innerHTML = `
          <div class="free-shipping-bar">
              <div class="progress" style="background-color:${this.progressBackground}">
                <div class="progress-bar" style="background-color:${bgColor};width:${progress}%"> 
                    <span class="truck-icon ${this.showTruck == "true" ? '' : 'd-none'}" style="border-color:${bgColor}">
                      <svg fill="${bgColor}" id="fi_17865314" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" data-name="Layer 1"><path d="m47.577 23.114-2.35-7.083c-.61-1.841-2.3-3.031-4.304-3.031h-6.292l.23-1.875c.102-.81-.138-1.612-.657-2.201-.518-.587-1.279-.924-2.09-.924h-24.237c-1.6 0-3.052 1.293-3.237 2.873l-.128 1c-.069.548.317 1.049.865 1.119.555.08 1.05-.317 1.119-.865l.129-1.011c.069-.595.654-1.116 1.252-1.116h24.237c.24 0 .449.088.59.247.143.162.204.384.173.632l-2.342 19.122h-26.252c0-.496-.355-.93-.86-.992-.549-.07-1.048.321-1.115.869l-.373 3.007c-.096.813.149 1.616.672 2.203.523.589 1.261.913 2.076.913h1.936c.015.993.343 1.919.989 2.647.771.872 1.869 1.353 3.089 1.353 2.288 0 4.368-1.765 4.836-4h17.327c.015.993.343 1.918.987 2.646.772.873 1.87 1.354 3.091 1.354 2.287 0 4.367-1.765 4.836-4h2.153c1.618 0 3.04-1.265 3.237-2.878l.768-6.263c.159-1.297.04-2.557-.354-3.745zm-2.145-.114h-6.026l.475-3.878c.007-.051.089-.122.13-.122h4.094zm-11.045-8h6.537c1.145 0 2.065.636 2.405 1.661l.113.339h-3.431c-1.057 0-1.985.825-2.114 1.878l-.49 4c-.067.548.098 1.092.453 1.493.354.399.869.628 1.416.628h6.674c.061.526.065 1.065-.003 1.616l-.415 3.384h-12.983l1.837-15zm-1.1 19h-1.228l.245-2h2.614c-.7.519-1.267 1.206-1.631 2zm-3.242 0h-14.539c-.158-.607-.44-1.17-.861-1.646-.118-.133-.256-.239-.388-.354h16.034l-.245 2zm-23.008 0h-2.355c-.24 0-.441-.084-.582-.242-.146-.163-.209-.391-.181-.635l.139-1.123h4.585c-.694.516-1.251 1.201-1.607 2zm6.596 1.378c-.174 1.422-1.519 2.622-2.937 2.622-.64 0-1.204-.241-1.592-.679-.394-.444-.566-1.048-.487-1.699.175-1.422 1.52-2.622 2.938-2.622.64 0 1.205.241 1.592.679.394.444.566 1.048.486 1.699zm26.241 0c-.175 1.422-1.52 2.622-2.937 2.622-.64 0-1.205-.241-1.593-.679-.394-.444-.566-1.048-.486-1.699.175-1.422 1.52-2.622 2.937-2.622.64 0 1.205.241 1.593.679.394.444.566 1.048.486 1.699zm4.053-1.378h-2.18c-.158-.607-.44-1.17-.861-1.646-.118-.133-.256-.239-.388-.354h4.79l-.108.878c-.073.598-.659 1.122-1.253 1.122z"></path><path d="m9.03 26c0-.553-.447-1-1-1h-7.03c-.553 0-1 .447-1 1s.447 1 1 1h7.03c.553 0 1-.447 1-1z"></path><path d="m4.087 20c-.553 0-1 .448-1 1s.447 1 1 1h4.561c.553 0 1-.448 1-1s-.447-1-1-1z"></path><path d="m2.175 17h8.08c.553 0 1-.448 1-1s-.447-1-1-1h-8.08c-.553 0-1 .448-1 1s.447 1 1 1z"></path></svg>
                    </span>
                </div>
              </div>
              <p style="color:${this.progressText}">${this.cartTotal < this.freeShippingThreshold ? 
                  `Spend <strong> $${remainingAmount.toFixed(2)} </strong> more to enjoy <span class="highlight">Free shipping!</span>` :
                  `🎉 Congrats! You have unlocked Free Shipping!`
              }</p>
          </div>
      `;
  }
}

// Define the custom element
customElements.define("free-shipping-bar", FreeShippingBar);


class CustomTab extends HTMLElement {
  connectedCallback() {
      this.render();
  }

  render() {
      const titles = this.querySelectorAll('[slot="tab-title"]');
      const contents = this.querySelectorAll('[slot="tab-content"]');

      this.innerHTML = `
          <div class="tabs">
              ${Array.from(titles).map((tab, index) => `
                  <button class="tab ${index === 0 ? "active" : ""}" data-index="${index}">${tab.innerHTML}</button>
              `).join('')}
          </div>
          <div class="tab-content-container">
              ${Array.from(contents).map((content, index) => `
                  <div class="tab-content ${index === 0 ? "active" : ""}" data-index="${index}">${content.innerHTML}</div>
              `).join('')}
          </div>
      `;

      this.querySelectorAll(".tab").forEach((tab) => {
          tab.addEventListener("click", (e) => this.switchTab(e));
      });
  }

  switchTab(event) {
      const index = event.target.getAttribute("data-index");

      this.querySelectorAll(".tab").forEach(tab => tab.classList.remove("active"));
      this.querySelectorAll(".tab-content").forEach(content => content.classList.remove("active"));

      event.target.classList.add("active");
      this.querySelector(`.tab-content[data-index="${index}"]`).classList.add("active");
  }
}

customElements.define("custom-tab", CustomTab);


class CustomAccordion extends HTMLElement {
  connectedCallback() {
      this.render();
  }

  render() {
      const items = Array.from(this.children);
      this.innerHTML = `
          <div class="accordion">
              ${items.map((item, index) => {
                 const isOpen = item.hasAttribute("open");
                return `<div class="accordion-item">
                      <button class="accordion-header" data-index="${index}">
                          <span class="accordion-title">${item.getAttribute("title")}</span>
                          <span class="arrow ${isOpen && "rotate"}">
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6.84171 4.40832C6.76361 4.48579 6.70161 4.57796 6.6593 4.67951C6.617 4.78106 6.59521 4.88998 6.59521 4.99999C6.59521 5.11 6.617 5.21892 6.6593 5.32047C6.70161 5.42202 6.76361 5.51419 6.84171 5.59166L10.6584 9.40832C10.7365 9.48579 10.7985 9.57796 10.8408 9.67951C10.8831 9.78106 10.9049 9.88998 10.9049 9.99999C10.9049 10.11 10.8831 10.2189 10.8408 10.3205C10.7985 10.422 10.7365 10.5142 10.6584 10.5917L6.84171 14.4083C6.76361 14.4858 6.70161 14.578 6.6593 14.6795C6.617 14.7811 6.59521 14.89 6.59521 15C6.59521 15.11 6.617 15.2189 6.6593 15.3205C6.70161 15.422 6.76361 15.5142 6.84171 15.5917C6.99785 15.7469 7.20906 15.834 7.42921 15.834C7.64937 15.834 7.86058 15.7469 8.01671 15.5917L11.8417 11.7667C12.3099 11.2979 12.5728 10.6625 12.5728 9.99999C12.5728 9.33749 12.3099 8.70208 11.8417 8.23332L8.01671 4.40832C7.86058 4.25311 7.64937 4.166 7.42921 4.166C7.20906 4.166 6.99785 4.25311 6.84171 4.40832Z" fill="black"/>
                            </svg>
                          </span>
                      </button>
                      <div class="accordion-content">
                          <div class="content-inner">${item.innerHTML}</div>
                      </div>
                  </div>
              `}).join('')}
          </div>
      `;

      this.querySelectorAll(".accordion-header").forEach(header => {
          header.addEventListener("click", (e) => this.toggleItem(e));
      });

      this.querySelectorAll(".accordion-content").forEach((content, index) => {
        const item = items[index];
        if (item.hasAttribute("open")) {
            content.style.maxHeight = content.scrollHeight + "px";
        }
      });
  }

  toggleItem(event) {
      const index = event.target.closest(".accordion-header").getAttribute("data-index");
      const content = this.querySelectorAll(".accordion-content")[index];
      const arrow = event.target.closest(".accordion-header").querySelector(".arrow");

      const isOpen = content.style.maxHeight;
      
      // Close all if only one open at a time
      this.querySelectorAll(".accordion-content").forEach((c, i) => {
          if (c !== content) {
              c.style.maxHeight = null;
              this.querySelectorAll(".arrow")[i].classList.remove("rotate");
          }
      });

      // Toggle current
      if (!isOpen) {
          content.style.maxHeight = content.scrollHeight + "px";
          arrow.classList.add("rotate");
      } else {
          content.style.maxHeight = null;
          arrow.classList.remove("rotate");
      }
  }
}

customElements.define("custom-accordion", CustomAccordion);



// Initialize Thumbnail Swiper

var thumbnailBottom = $(".dz-product-media").hasClass("thumbnail_bottom");

window.thumbnailSwiper = new Swiper('.thumbnail-swiper', {
  spaceBetween: 15,
  direction: (window.innerWidth > 769 ) ?  "vertical" : "horizontal",
  slideToClickedSlide: true,
  navigation: {
    nextEl: '.swiper-thumb-button-next',
    prevEl: '.swiper-thumb-button-prev',
  },
  breakpoints: {
    320: {
      slidesPerView: 4,
    },
    769: {
      slidesPerView: "auto",
    }
  },
  on: {
    resize: function () {
      if(!thumbnailBottom){
        thumbnailSwiper.changeDirection(getDirection());
      }
    },
    init: function () {
      checkScroll(this);
      if(thumbnailBottom){
        this.changeDirection('horizontal');
      }
    },
  }
});

function getDirection() {
  var direction = window.innerWidth < 769 ? 'horizontal' : 'vertical';
  return direction;
}

// Initialize Main Swiper with thumbnails as thumbs
window.mainSwiper = new Swiper('.main-swiper', {
  slidesPerView: 1,
  spaceBetween: 10,
  navigation: {
    nextEl: '.swiper-product-btn-next',
    prevEl: '.swiper-product-btn-prev',
  },
  thumbs: {
    swiper: $('.thumbnail-swiper').length > 0 && thumbnailSwiper,
  },
  on: {
    slideChange: function () {
      const activeSlide = this.slides[this.activeIndex];
      if (activeSlide.querySelector("product-model")) {
        this.allowTouchMove = false;
      } else {
        this.allowTouchMove = true;
      }
    },
  },
});


// Function to check if Swiper can scroll
function checkScroll(swiper) {
  const swiperWrapper = swiper.wrapperEl;
  const swiperContainer = swiper.el;
  const totalHeight = swiperWrapper.scrollHeight;
  const visibleHeight = swiperContainer.clientHeight;
  if (totalHeight > visibleHeight) {
    swiperContainer.classList.remove("no-scroll");
  } else {
    swiperContainer.classList.add("no-scroll");
  }
}

window.handleProductVariant = function(){
  document.querySelectorAll('.product-item').forEach(product => {
    const productId = product.dataset.productId;
    const variantDataScript = document.querySelector(`#variant-data-${productId}`);
    const productImage = document.querySelector(`#product-image-${productId} img`);
    
    const variantIdElement = product.querySelectorAll(".cart-btn");
    
    // Parse variant data
    let variants = [];
    try {
      variants = JSON.parse(variantDataScript.textContent);
    } catch (error) {
      console.error(`Error parsing variant data for product ${productId}:`, error);
    }
    
    if (!variants.length) {
      console.warn(`No variants found for product ${productId}`);
      return;
    }
    
    // Dynamically determine indices for Color and Size
    const colorOptions = document.querySelectorAll(`input[name="color-option-${productId}"]`);
    const sizeOptions = document.querySelectorAll(`input[name="size-option-${productId}"]`);
    
    // Helper function to update the variant information

    function updateVariant() {
      const selectedColor = document.querySelector(`input[name="color-option-${productId}"]:checked`)?.value;
      const selectedSize = document.querySelector(`input[name="size-option-${productId}"]:checked`)?.value;
      
      if (selectedColor && selectedSize) {
        const matchingVariant = variants.find(variant => {
          return (
            variant.options.includes(selectedColor) &&
            variant.options.includes(selectedSize)
          );
        });
        
        if (matchingVariant) {
          // Update product image
          productImage.srcset = matchingVariant.featured_image.src;
          productImage.src = matchingVariant.featured_image.src;
          productImage.alt = matchingVariant.title;

          
          // Update variant ID display
          if(variantIdElement){
            variantIdElement.forEach((btn) => {
              if(matchingVariant.available == false){
                btn.setAttribute("disabled","");
              }else{
                btn.removeAttribute("disabled");
              }
              btn.setAttribute("data-variant-id",matchingVariant.id);
            })
          }
        }
      }else if(selectedColor){
        const matchingVariant = variants.find(variant => {
          return (
            variant.options.includes(selectedColor)
          );
        });

        if (matchingVariant) {
          // Update product image

          productImage.srcset = matchingVariant.featured_image.src;
          productImage.src = matchingVariant.featured_image.src;
          productImage.alt = matchingVariant.title;

          // Update variant ID display
          if(variantIdElement){
            variantIdElement.forEach((btn) => {
              if(matchingVariant.available == false){
                btn.setAttribute("disabled","");
              }else{
                btn.removeAttribute("disabled");
              }
              btn.setAttribute("data-variant-id",matchingVariant.id);
            })
          }
        }
      }else if(selectedSize){
        const matchingVariant = variants.find(variant => {
          return (
            variant.options.includes(selectedSize)
          );
        });

        if (matchingVariant) {

          // Update variant ID display
          if(variantIdElement){
            variantIdElement.forEach((btn) => {
              if(matchingVariant.available == false){
                btn.setAttribute("disabled","");
              }else{
                btn.removeAttribute("disabled");
              }
              btn.setAttribute("data-variant-id",matchingVariant.id);
            })
          }
        }
      }
    }

    // Initialize default variant on page load
    updateVariant();

    // Add event listeners to color and size options
    colorOptions.forEach(colorOption => {
      colorOption.addEventListener('change', updateVariant);
    });
    sizeOptions.forEach(sizeOption => {
      sizeOption.addEventListener('change', updateVariant);
    });
  });
}

window.handleImageLazyLoad = function() { 
  const lazyImages = document.querySelectorAll(".lazy-image");

  lazyImages.forEach((img) => {
    img.closest(".image-placeholder").classList.add('loading');
    img.addEventListener("load", () => {
      img.style.opacity = 1;
      img.closest(".image-placeholder").classList.remove("loading");
      img.closest(".image-placeholder").classList.add("loaded");
    });
    // console.log(img);
    img.src = img.dataset.src;
  });
}


window.handleProductInfiniteScroll = function() {
    let isLoading = false;
    let currentPage = 1;
    const loader = document.querySelector(".loader");

    if(loader){

      const productContainer = document.querySelector(".collection-products");
      const loadMoreBtn = document.getElementById("load-more-btn");
      const currentCountElement = document.getElementById("current-count");

      // Toggle infinite scroll with this variable
      const infiniteScroll = loader.hasAttribute('data-infinite-scroll');
      const enableInfiniteScroll = infiniteScroll; // Set to `false` to disable infinite scrolling

      // Get total products dynamically
      const totalProducts = parseInt(document.querySelector("[data-total-products]").dataset.totalProducts);

      const urlParams = new URLSearchParams(window.location.search);

      const loadMoreProducts = async () => {
          if (isLoading) return;

          isLoading = true;

          loadMoreBtn.classList.add('loading');
          loadMoreBtn.setAttribute("disabled","");

          currentPage++;

          urlParams.set("page", currentPage);
          const nextPageUrl = `${window.location.pathname}?${urlParams.toString()}`;

          try {
              const response = await fetch(nextPageUrl);
              const html = await response.text();
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              const newProducts = doc.querySelector(".collection-products");

              if (newProducts) {
                  productContainer.append(...newProducts.children);

                  const productArea = document.querySelector(".product-main-container");
                  if(productArea){
                    handleProductGrid();
                  }

                  handleProductVariant();
                  handleImageLazyLoad();

                  // Update visible product counts
                  const currentCount = productContainer.querySelectorAll(".grid__item").length;
                  currentCountElement.textContent = currentCount; 

                  loadMoreBtn.classList.remove('loading');
                  loadMoreBtn.removeAttribute("disabled");

                  // Stop loading if all products are loaded
                  if (currentCount >= totalProducts) {
                    loadMoreBtn.setAttribute("disabled","");
                    loadMoreBtn.textContent = loadMoreBtn.dataset.noMoreProducts;
                      if (enableInfiniteScroll) window.removeEventListener("scroll", handleScroll);
                  }
              } else {
                  if (enableInfiniteScroll) window.removeEventListener("scroll", handleScroll);
              }
          } catch (error) {
              console.error("Failed to load more products:", error);
          } finally {
              isLoading = false;
          }
      };

      const handleScroll = () => {
          const scrollPosition = window.scrollY + window.innerHeight;
          const threshold = document.body.offsetHeight - 400;

          if (scrollPosition > threshold) {
              loadMoreProducts();
          }
      };

      // Add event listener for scroll only if infinite scroll is enabled
      if (enableInfiniteScroll) {
          window.addEventListener("scroll", handleScroll);
      }

      // Always allow manual "Load More" button click
      if(loadMoreBtn){
        loadMoreBtn.addEventListener("click", loadMoreProducts);
      }
    }
}

window.handleTooltip = function(){
  // Create a single tooltip element and append it to the body
  const tooltip = document.createElement("div");
  tooltip.className = "custom-tooltip";
  document.body.appendChild(tooltip);

  // Function to show the tooltip
  function showTooltip(event) {
    const content = event.target.getAttribute("data-dz-tooltip");
    if (!content) return;

    // Update tooltip content
    tooltip.textContent = content;

    // Calculate position
    const elementRect = event.target.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    var top = elementRect.top - tooltipRect.height - 10; // 10px offset
    var left = elementRect.left + elementRect.width / 2 - tooltipRect.width / 2;
    tooltip.classList.remove('left');
    
    if(event.target.hasAttribute('tooltip-left')){
      tooltip.classList.add('left');
      top = elementRect.top + elementRect.height / 2 - tooltipRect.height / 2; // 10px offset
      left = elementRect.left - tooltipRect.width - 10;
    }

    // Set tooltip position
    tooltip.style.top = `${top}px`;
    tooltip.style.left = `${left}px`;

    // Show tooltip
    tooltip.style.opacity = "1";
    tooltip.style.pointerEvents = "none";
  }

  // Function to hide the tooltip
  function hideTooltip() {
    tooltip.style.opacity = "0";
    tooltip.style.pointerEvents = "none";
  }

  // Attach event listeners to elements with the data-dz-tooltip attribute
  document.addEventListener(
  "mouseenter",
	  (event) => {
		const target = event.target;
		if (target instanceof Element && target.hasAttribute("data-dz-tooltip")) {
		  showTooltip(event);
		}
	  },
	  true
	);

	document.addEventListener(
	  "mouseleave",
	  (event) => {
		const target = event.target;
		if (target instanceof Element && target.hasAttribute("data-dz-tooltip")) {
		  hideTooltip();
		}
	  },
	  true
	);

}

window.productSoldOut = function(){
  var wrapper = $('[data-sold-out-product]');
  if (wrapper.length > 0) {
      var numbersProductList = wrapper.data('item').toString().split(','),
          numbersProductItem = Math.floor(Math.random() * numbersProductList.length),
          numbersHoursList = wrapper.data('hours').toString().split(','),
          numbersHoursItem = Math.floor(Math.random() * numbersHoursList.length);

      wrapper.find('[data-sold-out-number]').text(numbersProductList[numbersProductItem]);
      wrapper.find('[data-sold-out-hours]').text(numbersHoursList[numbersHoursItem]);
      wrapper.show();
  }
}

window.handleModal = function(){
  $(document).on('click', '[data-dz-modal]', function () {
    $($(this).data('dz-modal')).addClass('show');
  });

  $(document).on('click', '.modal-close', function () {
    $(this).closest('.modal').removeClass('show');
  });

  $(document).on('click','.notify-btn',function(){
    var productTitle = $(this).attr('data-product');
    var productUrl = $(this).attr('data-product-url');
    var shopUrl = $(this).attr('data-shop');
    $('.notify-modal .notify-form-body').val(window.notify_me.body.replace('[title]', productTitle).replace('[productUrl]', shopUrl + productUrl));
  })

}

window.handleTermCheckbox = function(){
  $(document).on('change','#term_checkbox',function() {
    if ($(this).is(':checked')) {
        $('#checkout').prop('disabled', false);
    } else {
        $('#checkout').prop('disabled', true);
    }
  });
  $(document).on('change','#term_checkbox2',function() {
    if ($(this).is(':checked')) {
        $('#checkout-product').removeClass('disabled');
    } else {
        $('#checkout-product').addClass('disabled');
    }
  });
  $(document).on('change','#term_checkbox3',function() {
    if ($(this).is(':checked')) {
        $('#quick-checkout-product').removeClass('disabled');
    } else {
        $('#quick-checkout-product').addClass('disabled');
    }
  });
}

$(document).ready(function () {
  handleModal();
  handleTermCheckbox();
});

$(window).on('load', function () {
  $('#page-loader').fadeOut('slow');
});

document.addEventListener("DOMContentLoaded", () => {

  handleProductVariant();
  handleImageLazyLoad();
  handleTooltip();
  productSoldOut();
  
});
